/**
 * Lage des Bestands für Vorstand und Geschäftsstelle: Kennzahlen wie in den Gebäudereitern (KpiTiles),
 * zwei Doughnuts — Bearbeitungsbedarf und Alter der Ausgaben, Anteile am Bestand — und je Richtlinie
 * Ausgabe, Gesetzesänderungen, offene Arbeit, Prüfstand und Glossar. Ein Segment filtert die Tabelle.
 *
 * Farben: Bedarf in den festen Statusfarben, nie allein — Legende mit Symbol, Wort und Zahl; Alter als
 * einfarbige Stufung (geprüft: geordnete Skala, Helligkeit stetig, hellstes Glied ≥ 2:1 zum Grund).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import { AlertTriangle, BookMarked, CheckCircle2, Clock, FileClock, Hourglass, Library, ListChecks, Loader2, Scale, Stamp, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toast } from "sonner";

import KpiTiles from "../components/KpiTiles";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { GUIDELINES_PATH } from "./guidelinesPath";
import { useGuidelinesAPI, type GuidelineDashboard, type GuidelineDashboardItem, type GuidelineNeed } from "./GuidelinesAPI";

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

const NEEDS: GuidelineNeed[] = ["revise", "review", "current"];
/** Feste Statusfarben (kritisch, Warnung, gut) mit Symbol — die Farbe trägt die Bedeutung nie allein. */
const NEED_STYLE: Record<GuidelineNeed, { color: string; icon: LucideIcon }> = {
  revise: { color: "#d03b3b", icon: AlertTriangle },
  review: { color: "#fab219", icon: FileClock },
  current: { color: "#0ca30c", icon: CheckCircle2 },
};

type AgeBand = "lt3" | "3to5" | "5to10" | "gt10";
const AGE_BANDS: AgeBand[] = ["lt3", "3to5", "5to10", "gt10"];
/** Einfarbig hell → dunkel: je älter, desto kräftiger. */
const AGE_COLORS = ["#86b6ef", "#3987e5", "#1c5cab", "#0d366b"];
const ageBand = (years: number | null): AgeBand | null => (years == null ? null : years < 3 ? "lt3" : years < 5 ? "3to5" : years < 10 ? "5to10" : "gt10");

type Filter = { kind: "need"; value: GuidelineNeed } | { kind: "age"; value: AgeBand } | null;

/** Doughnut mit Summe in der Mitte; die Legende daneben trägt Wort und Zahl (und beim Bedarf das Symbol). */
function DonutCard(props: {
  title: string;
  hint: string;
  labels: string[];
  values: number[];
  colors: string[];
  icons?: LucideIcon[];
  totalLabel: string;
  selected: number | null;
  onSelect: (index: number | null) => void;
}) {
  const total = props.values.reduce((a, b) => a + b, 0);
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-lg border bg-card p-4">
      <div>
        <div className="text-sm font-semibold">{props.title}</div>
        <div className="text-xs text-muted-foreground">{props.hint}</div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-[170px] max-w-full">
          <Chart
            type="donut"
            height={170}
            width="100%"
            series={props.values}
            options={{
              chart: {
                type: "donut",
                fontFamily: "inherit",
                animations: { enabled: false },
                events: { dataPointSelection: (_e: unknown, _c: unknown, cfg: any) => props.onSelect(cfg?.dataPointIndex === props.selected ? null : cfg?.dataPointIndex ?? null) },
              },
              labels: props.labels,
              colors: props.colors,
              legend: { show: false },
              dataLabels: { enabled: false },
              // Lücke zwischen den Segmenten in der Grundfarbe der Karte.
              stroke: { width: 2, colors: ["hsl(var(--card))"] },
              states: { active: { filter: { type: "none" } } },
              tooltip: { y: { formatter: (v: number) => `${v} von ${total}` } },
              plotOptions: {
                pie: {
                  expandOnClick: false,
                  donut: {
                    size: "68%",
                    labels: {
                      show: true,
                      name: { show: true, fontSize: "11px", offsetY: 20, color: "hsl(var(--muted-foreground))" },
                      value: { show: true, fontSize: "26px", fontWeight: 600, offsetY: -6, color: "hsl(var(--foreground))" },
                      total: { show: true, showAlways: true, label: props.totalLabel, fontSize: "11px", color: "hsl(var(--muted-foreground))", formatter: () => String(total) },
                    },
                  },
                },
              },
            }}
          />
        </div>
        <ul className="flex min-w-[10rem] flex-1 flex-col gap-1 text-sm">
          {props.labels.map((label, i) => {
            const Icon = props.icons?.[i];
            const active = props.selected === i;
            return (
              <li key={label}>
                <button
                  type="button"
                  onClick={() => props.onSelect(active ? null : i)}
                  disabled={!props.values[i]}
                  className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-accent disabled:opacity-50 ${active ? "bg-accent font-medium" : ""}`}
                >
                  <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: props.colors[i] }} />
                  {Icon ? <Icon size={13} aria-hidden className="shrink-0 text-muted-foreground" /> : null}
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  <span className="tabular-nums text-muted-foreground">{props.values[i]}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function NeedBadge({ item }: { item: GuidelineDashboardItem }) {
  const { t } = useTranslation();
  const { color, icon: Icon } = NEED_STYLE[item.need];
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-1 text-xs font-medium">
        <Icon size={13} aria-hidden style={{ color }} />
        {t(`guidelinesPage.dashboard.need.${item.need}`)}
      </span>
      {item.reasons.length ? <span className="text-xs text-muted-foreground">{item.reasons.join(" · ")}</span> : null}
    </div>
  );
}

/** Zahl mit Warnsymbol, wenn sie Arbeit bedeutet — nie nur über Farbe. */
function Count({ n, warn, label }: { n: number; warn?: boolean; label?: string }) {
  if (!n) return <span className="text-muted-foreground">0</span>;
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      {warn ? <AlertTriangle size={12} className="text-amber-600" aria-hidden /> : null}
      {n}
      {label ? <span className="text-xs text-muted-foreground">{label}</span> : null}
    </span>
  );
}

export function DashboardPanel({ customer }: { customer: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useGuidelinesAPI(customer);
  const [data, setData] = useState<GuidelineDashboard | null>(null);
  const [filter, setFilter] = useState<Filter>(null);

  useEffect(() => {
    api.getDashboard().then(setData).catch((e) => toast.error(errorText(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const rows = useMemo(
    () => (data?.items ?? []).filter((x) => !filter || (filter.kind === "need" ? x.need === filter.value : ageBand(x.ageYears) === filter.value)),
    [data, filter]
  );

  if (!data)
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" />
        {t("guidelinesPage.dashboard.loading")}
      </div>
    );

  const tt = data.totals;
  const nf = (n: number | null) => (n == null ? "–" : n.toLocaleString("de-DE"));
  const open = (id: string, tab?: string) => navigate(`${GUIDELINES_PATH}/${id}${tab ? `?tab=${tab}` : ""}`);
  const ageCounts = AGE_BANDS.map((b) => data.items.filter((x) => ageBand(x.ageYears) === b).length);

  return (
    <div className="flex flex-col gap-4" data-testid="dashboard-panel">
      <KpiTiles
        items={[
          { label: t("guidelinesPage.dashboard.guidelines"), value: nf(tt.guidelines), hint: t("guidelinesPage.dashboard.needHint", { revise: tt.need.revise, review: tt.need.review }), icon: Library, tone: "primary" },
          { label: t("guidelinesPage.dashboard.tile.age"), value: nf(tt.averageAge), unit: t("guidelinesPage.dashboard.yearsUnit"), hint: t("guidelinesPage.dashboard.years"), icon: Hourglass, tone: (tt.averageAge ?? 0) >= 5 ? "warn" : "neutral" },
          { label: t("guidelinesPage.dashboard.tile.law"), value: nf(tt.changedRegulations), hint: t("guidelinesPage.dashboard.lawHint"), icon: Scale, tone: tt.changedRegulations ? "bad" : "good" },
          { label: t("guidelinesPage.dashboard.tile.unchecked"), value: nf(tt.uncheckedRequirements), hint: t("guidelinesPage.dashboard.uncheckedHint", { n: tt.linksProposed }), icon: ListChecks, tone: tt.uncheckedRequirements ? "warn" : "good" },
          { label: t("guidelinesPage.dashboard.tile.glossary"), value: nf(tt.glossaryDeviations), hint: t("guidelinesPage.dashboard.glossaryHint"), icon: BookMarked, tone: tt.glossaryDeviations ? "warn" : "good" },
          { label: t("guidelinesPage.dashboard.tile.approval"), value: nf(tt.awaitingApproval), hint: t("guidelinesPage.dashboard.awaitingHint"), icon: Stamp, tone: tt.awaitingApproval ? "primary" : "neutral" },
        ]}
      />

      <div className="grid gap-3 lg:grid-cols-2">
        <DonutCard
          title={t("guidelinesPage.dashboard.needTitle")}
          hint={t("guidelinesPage.dashboard.needChartHint")}
          labels={NEEDS.map((n) => t(`guidelinesPage.dashboard.need.${n}`))}
          values={NEEDS.map((n) => tt.need[n])}
          colors={NEEDS.map((n) => NEED_STYLE[n].color)}
          icons={NEEDS.map((n) => NEED_STYLE[n].icon)}
          totalLabel={t("guidelinesPage.dashboard.guidelines")}
          selected={filter?.kind === "need" ? NEEDS.indexOf(filter.value) : null}
          onSelect={(i) => setFilter(i == null ? null : { kind: "need", value: NEEDS[i] })}
        />
        <DonutCard
          title={t("guidelinesPage.dashboard.ageTitle")}
          hint={t("guidelinesPage.dashboard.ageChartHint")}
          labels={AGE_BANDS.map((b) => t(`guidelinesPage.dashboard.ageBand.${b}`))}
          values={ageCounts}
          colors={AGE_COLORS}
          totalLabel={t("guidelinesPage.dashboard.guidelines")}
          selected={filter?.kind === "age" ? AGE_BANDS.indexOf(filter.value) : null}
          onSelect={(i) => setFilter(i == null ? null : { kind: "age", value: AGE_BANDS[i] })}
        />
      </div>

      {filter ? (
        <div className="flex items-center gap-2 text-sm">
          <Clock size={14} className="text-muted-foreground" aria-hidden />
          {t("guidelinesPage.dashboard.filtered", {
            label: filter.kind === "need" ? t(`guidelinesPage.dashboard.need.${filter.value}`) : t(`guidelinesPage.dashboard.ageBand.${filter.value}`),
            n: rows.length,
          })}
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setFilter(null)}>
            <X size={13} className="mr-1" />
            {t("guidelinesPage.dashboard.clearFilter")}
          </Button>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full min-w-[64rem] text-sm">
          <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.guideline")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.need")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.edition")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.law")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.work")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.requirements")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.links")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.glossary")}</th>
              <th className="px-3 py-2 font-medium">{t("guidelinesPage.dashboard.col.newEdition")}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((x) => (
              <tr key={x.documentId} className="align-top" data-dashboard={x.title}>
                <td className="relative px-3 py-2 pl-4">
                  <span aria-hidden className="absolute inset-y-2 left-0 w-1 rounded-r" style={{ background: NEED_STYLE[x.need].color }} />
                  <button type="button" className="whitespace-nowrap font-medium text-primary hover:underline" onClick={() => open(x.documentId)}>
                    {x.title}
                  </button>
                  <div className="text-xs text-muted-foreground">{x.workingGroup?.name ?? t("guidelinesPage.dashboard.noGroup")}</div>
                </td>
                <td className="max-w-[16rem] px-3 py-2">
                  <NeedBadge item={x} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                  {x.edition ?? "–"}
                  <div className="text-xs text-muted-foreground">{x.ageYears != null ? t("guidelinesPage.dashboard.age", { n: x.ageYears }) : ""}</div>
                </td>
                <td className="px-3 py-2">
                  <button type="button" className="text-left hover:underline" onClick={() => open(x.documentId, "review")}>
                    <Count n={x.law.changedRegulations} warn label={t("guidelinesPage.dashboard.regulations")} />
                  </button>
                  <div className="text-xs text-muted-foreground">{t("guidelinesPage.dashboard.lawDetail", { editions: x.law.newerEditions, other: x.law.otherChanges })}</div>
                </td>
                <td className="px-3 py-2 text-xs">{t("guidelinesPage.dashboard.workDetail", { drafts: x.drafts, suggestions: x.openSuggestions, comments: x.openDiscussions })}</td>
                <td className="px-3 py-2">
                  <button type="button" className="hover:underline" onClick={() => open(x.documentId, "requirements")}>
                    <Count n={x.uncheckedRequirements} warn />
                    <span className="text-xs text-muted-foreground"> / {x.requirements}</span>
                  </button>
                  <div className="text-xs text-muted-foreground">{t("guidelinesPage.dashboard.unchecked")}</div>
                </td>
                <td className="px-3 py-2">
                  <button type="button" className="hover:underline" onClick={() => open(x.documentId, "links")}>
                    <Count n={x.linksProposed} warn label={t("guidelinesPage.dashboard.open")} />
                  </button>
                  <div className="text-xs text-muted-foreground">{t("guidelinesPage.dashboard.confirmed", { n: x.linksConfirmed })}</div>
                </td>
                <td className="px-3 py-2">
                  <Count n={x.glossaryDeviations} warn label={t("guidelinesPage.dashboard.deviating")} />
                </td>
                <td className="px-3 py-2">
                  {x.newEdition ? (
                    <Badge variant={x.newEdition.status === "submitted" ? "default" : "secondary"} className="px-1.5 py-0 text-[11px] font-normal">
                      {x.newEdition.label ?? ""} · {t(`guidelinesPage.dashboard.editionStatus.${x.newEdition.status}`, { defaultValue: x.newEdition.status })}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">–</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{t("guidelinesPage.dashboard.note")}</p>
    </div>
  );
}
